import { initFirebase, type CitiboxFirebaseConfig } from '@citibox-panel/auth';
import { readRuntimeConfig, nonEmpty } from './runtimeConfig';

export type BootstrapResult =
  | { ok: true }
  | { ok: false; missing: readonly string[] };

/** Values like `<your-api-key>` are unfilled template placeholders, not config. */
const PLACEHOLDER = /^<.*>$/;

function providedValue(value: string | undefined): string | undefined {
  const v = nonEmpty(value);
  return v !== undefined && !PLACEHOLDER.test(v) ? v : undefined;
}

const REQUIRED_KEYS = [
  'FIREBASE_API_KEY',
  'FIREBASE_AUTH_DOMAIN',
  'FIREBASE_PROJECT_ID',
  'FIREBASE_APP_ID',
] as const;

export function bootstrapFirebase(): BootstrapResult {
  const cfg = readRuntimeConfig();
  const apiKey = providedValue(cfg.FIREBASE_API_KEY);
  const authDomain = providedValue(cfg.FIREBASE_AUTH_DOMAIN);
  const projectId = providedValue(cfg.FIREBASE_PROJECT_ID);
  const appId = providedValue(cfg.FIREBASE_APP_ID);

  if (
    apiKey === undefined ||
    authDomain === undefined ||
    projectId === undefined ||
    appId === undefined
  ) {
    // Don't throw: the SPA shell must render even before Firebase is wired up
    // (e.g. first deploy to a new environment). The caller decides how to
    // degrade the auth UI.
    const missing = REQUIRED_KEYS.filter((key) => providedValue(cfg[key]) === undefined);
    console.warn('[auth] bootstrap.missing', { missing });
    return { ok: false, missing };
  }

  const config: CitiboxFirebaseConfig = {
    apiKey,
    authDomain,
    projectId,
    appId,
    allowedDomain: nonEmpty(cfg.ALLOWED_DOMAIN) ?? 'citibox.com',
  };
  initFirebase(config);
  return { ok: true };
}
